import React, { useState } from 'react';
import { Conversation, AgentDefinition } from '../types';

interface ConversationSidebarProps {
  conversations: Conversation[];
  agents: AgentDefinition[];
  activeConversationId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
}

function relativeDate(iso: string): 'today' | 'yesterday' | 'older' {
  const d = new Date(iso);
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - d.getTime()) / 86400000);
  if (diffDays === 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  return 'older';
}

function relativeTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diffMin = Math.floor((now.getTime() - d.getTime()) / 60000);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  return `${diffDays}d ago`;
}

export const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  conversations,
  agents,
  activeConversationId,
  onSelect,
  onNew,
  onDelete,
}) => {
  const [search, setSearch] = useState('');
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  const filtered = conversations.filter((c) =>
    c.title.toLowerCase().includes(search.toLowerCase())
  );

  const grouped: Record<string, Conversation[]> = { today: [], yesterday: [], older: [] };
  for (const c of filtered) {
    grouped[relativeDate(c.lastMessageAt)].push(c);
  }

  const renderGroup = (label: string, items: Conversation[]) => {
    if (!items.length) return null;
    return (
      <div key={label} className="chat-sidebar__group">
        <div className="chat-sidebar__group-label">{label.toUpperCase()}</div>
        {items.map((conv) => {
          const agent = agents.find((a) => a.id === conv.lastAgentId);
          const isActive = conv.id === activeConversationId;
          return (
            <div
              key={conv.id}
              className={`chat-sidebar__item${isActive ? ' chat-sidebar__item--active' : ''}`}
              onClick={() => onSelect(conv.id)}
              onMouseEnter={() => setHoveredId(conv.id)}
              onMouseLeave={() => setHoveredId(null)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && onSelect(conv.id)}
              aria-label={`Open conversation: ${conv.title}`}
            >
              <div
                className="chat-sidebar__avatar"
                style={{ background: agent?.color ?? '#8b949e' }}
              >
                <span>{agent?.icon ?? '🤖'}</span>
              </div>
              <div className="chat-sidebar__item-body">
                <div className="chat-sidebar__item-title">{conv.title}</div>
                <div className="chat-sidebar__item-meta">
                  {agent?.name ?? 'Unknown'} · {relativeTime(conv.lastMessageAt)}
                </div>
              </div>
              {hoveredId === conv.id && (
                <button
                  className="chat-sidebar__item-delete"
                  onClick={(e) => { e.stopPropagation(); onDelete(conv.id); }}
                  aria-label="Delete conversation"
                  title="Delete"
                >
                  🗑
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="chat-sidebar">
      <div className="chat-sidebar__search-wrap">
        <input
          className="chat-sidebar__search"
          type="text"
          placeholder="Search conversations..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          aria-label="Search conversations"
        />
      </div>
      <button className="chat-sidebar__new-btn" onClick={onNew}>
        + New Conversation
      </button>
      <div className="chat-sidebar__list">
        {renderGroup('today', grouped.today)}
        {renderGroup('yesterday', grouped.yesterday)}
        {renderGroup('older', grouped.older)}
        {filtered.length === 0 && (
          <div className="chat-sidebar__empty">No conversations found.</div>
        )}
      </div>
    </div>
  );
};
